/* eslint-disable @typescript-eslint/naming-convention */
import { takeLatest, put, call, select } from 'redux-saga/effects';
import {
  initialize,
  arrayRemoveAll,
  arrayPush,
  change,
  reset,
} from 'redux-form';
import { delay } from 'redux-saga';

import {
  LIBRARY_MODIFIER_ASK_DELETE_MODIFIER,
  LIBRARY_MODIFIER_CANCEL_DELETE_MODIFIER,
  LIBRARY_MODIFIER_DELETE_MODIFIER,
  LIBRARY_MODIFIER_MODIFIERS_REQUEST,
  LIBRARY_MODIFIER_ON_CHANGE_OUTLET_IDS,
  LIBRARY_MODIFIER_ON_CLICK_CANCEL,
  LIBRARY_MODIFIER_ON_CLICK_CREATE_MODIFIER,
  LIBRARY_MODIFIER_ON_CLICK_ROW,
  LIBRARY_MODIFIER_PAGE_REQUEST,
  LIBRARY_MODIFIER_SUBMIT_FORM_CREATE,
  LIBRARY_MODIFIER_SUBMIT_FORM_CREATE_ALL_OUTLET,
  LIBRARY_MODIFIER_SUBMIT_FORM_UPDATE,
  LIBRARY_MODIFIER_ON_CLICK_APPLY_SET_ITEM,
  LIBRARY_MODIFIER_CANCEL_APPLY_SET_ITEM,
  LIBRARY_MODIFIER_CONFIRM_APPLY_SET_ITEM,
  LIBRARY_MODIFIER_FILL_MODIFIER_FORM,
  LIBRARY_MODIFIER_APPLY_SET_ITEM_ITEMS_REQUEST,
  LIBRARY_MODIFIER_ON_CLICK_MANAGE_MODIFIER_OPTIONS,
  LIBRARY_MODIFIER_ON_CANCEL_MANAGE_MODIFIER_OPTIONS,
  LIBRARY_MODIFIER_ON_CONFIRM_MANAGE_MODIFIER_OPTIONS,
  LIBRARY_MODIFIER_EMPTY_MODIFIER_FORM,
  LIBRARY_MODIFIER_APPLY_SET_ITEM_FILL_FORM,
  LIBRARY_MODIFIER_GET_INGREDIENTS_LIST,
  LIBRARY_MODIFIER_FETCH_INGREDIENTS_LIST,
} from '../../actions/library/modifier/constants';
import * as act from '../../actions';
import * as actNotif from '../../actions/notifications';
import * as actModifier from '../../actions/library/modifier';
import { setSelectedOutletIds } from '../../actions/actionBar';
import { showPageLoadingSpinner } from '../../actions/loadingSpinner';
import * as actOutletList from '../../actions/outletPages/list';

import * as apiOutlets from '../../api/outlets';
import * as apiModifier from '../../api/modifiers';
import * as apiItems from '../../api/items';
import {
  getSelectedOutletIdFromLocalStorage,
  setSelectedOutletIdToLocalStorage,
} from '../../utils/selectedOutletId';
import { SetIngredientListData } from '../../reducers/library/types';
import { getSubscriptionStatus } from '../../api/feature/getSubscriptionStatus';
import { ErrorCode } from '../../api/modifiers/constants';
import { assertErrorHasMessage } from '../../utils/assert';
import * as clevertapLibraryModifier from '../../clevertap/library/modifier';

const getCloneNotification = (state) =>
  state.outletPages.list.showCloneOutletNotification.notification;
const getIngredientsList = (state) => state.library.modifier.ingredientsList;
const getUnleashItems = (state) => state.unleashFeatureFlags.items;
const getUser = (state) => state.moka.user;

function* getModifierStockFlag() {
  const allFlags = yield select(getUnleashItems);
  const modifierStockFlag = allFlags['modifier-stock-ui'];
  const isEnabled = modifierStockFlag?.isEnabled || false;

  return isEnabled;
}

function* fillSearchFormFromOutletIds(outletIds) {
  let assignedOutletIds: any[] = [];
  if (outletIds.length > 1) {
    assignedOutletIds = [outletIds[0]];
  } else {
    assignedOutletIds = outletIds;
  }
  const data = { outletIds: assignedOutletIds };
  yield put(initialize('search', data));
}

function* fillModifierFormFlow(request) {
  const { modifier } = request.payload;
  const isModifierStockEnabled = yield call(getModifierStockFlag);

  const form: any = {
    id: modifier.id,
    name: modifier.name,
    options: modifier.active_options.map((m) => {
      const { id, name } = m;
      let { price } = m;
      price = price || price === 0 ? price.toString() : price;

      const option: any = { id, name, price };

      if (isModifierStockEnabled) {
        const { ingredient } = m;
        option.ingredient_quantity = (ingredient?.quantity || '').toString();
        option.ingredient = ingredient;
      }

      return option;
    }),
  };

  if (isModifierStockEnabled) {
    form.track_stock = modifier.track_stock;
  }

  form.mandatory = modifier.option_mandatory || false;
  form.max_option_changed = modifier.max_option_changed || false;
  form.min_no_of_options = modifier.min_no_of_options;
  form.max_no_of_options = modifier.max_no_of_options;
  form.option_setting_toggle = modifier.option_setting_toggle;

  yield put(initialize('modifier', form));
}

function* fillApplySetItemFormFlow(request) {
  const {
    payload: { modifier },
  } = request;

  const form = {
    id: modifier.id,
    itemIds: modifier.active_item_modifiers.map((itemModifier) => {
      return itemModifier.item_id;
    }),
  };
  yield put(initialize('applySetItem', form));
}

function* emptyModifierFormFlow() {
  const isModifierStockEnabled = yield call(getModifierStockFlag);
  const form: any = {
    id: null,
    name: null,
    options: [{ name: null, price: null }],
  };

  const option: any = {
    name: null,
    price: null,
  };

  form.min_no_of_options = 1;
  form.max_no_of_options = 1;
  form.mandatory = false;
  form.max_option_changed = false;

  if (isModifierStockEnabled) {
    option.ingredient_quantity = '';
    option.ingredient = null;
  }

  form.options = [option];

  yield put(initialize('modifier', form));
}

function* openRightColumn() {
  yield put(actModifier.openRightColumn());
}

function* closeRightColumn() {
  yield put(actModifier.closeRightColumn());
}

function* getCloneOutletNotification() {
  const notification = yield select(getCloneNotification);
  return notification;
}

function* setModifierForm(modifier) {
  const modifierData = { ...modifier };

  const minOptions = modifierData.min_no_of_options || 0;
  const maxOptions = modifierData.max_no_of_options || 0;
  const optionsLength = modifierData.modifier_options.length;

  modifierData.min_no_of_options = minOptions || 1;
  modifierData.max_no_of_options = maxOptions || optionsLength;
  modifierData.option_mandatory = minOptions !== 0;
  modifierData.max_option_changed = maxOptions !== 0;
  modifierData.option_setting_toggle = minOptions !== 0 || maxOptions !== 0;

  yield call(fillModifierFormFlow, {
    payload: {
      modifier: modifierData,
    },
  });
  yield put(actModifier.setModifierDetail(modifierData));
}

function* ingredientsListInfiniteScroll(request) {
  // Delay to prevent too many network requests
  if (request.keyword) {
    yield call(delay, 500);
  }

  try {
    const currentIngredientList = yield select(getIngredientsList);

    const isFirstPage = request.page === 1;
    const isLoading = isFirstPage && !currentIngredientList.isEmpty;
    yield put(
      actModifier.setIngredientsList({
        ...currentIngredientList,
        isLoading,
      })
    );

    const { data } = yield call(apiModifier.getIngredientsList, request);
    const { results, total_count, total_pages, page } = data;

    const isEmpty = results.length === 0;
    const isNoKeyword = !request.keyword;
    const isAllIngredient = !request.type || request.type === 'ALL';
    const isNotHaveIngredients =
      (isAllIngredient && isEmpty && isNoKeyword) ||
      currentIngredientList.isEmpty;

    const ingredientsList: SetIngredientListData = {
      results: request.previousItems.concat(results),
      total_pages,
      total_count,
      currentPage: page,
      isLoading: false,
      isEmpty: isNotHaveIngredients,
    };

    yield put(actModifier.setIngredientsList(ingredientsList));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  }
}

export function* pageRequest() {
  // reset...
  yield put(actModifier.setModifiers([]));
  yield put(showPageLoadingSpinner(true));
  yield put(actModifier.setModifierDetail(null));
  yield put(actModifier.setOutlets([]));
  yield put(actModifier.closeRightColumn());
  yield put(act.showProgressBar());
  yield put(actModifier.emptyModifierForm());
  yield put(actModifier.hideUnableToSaveAllOutletModal());
  try {
    const response = yield call(apiOutlets.outlets);
    yield put(actModifier.setOutlets(response.data));
    const { data: subscription } = yield call(getSubscriptionStatus, {});

    const ingredientSubscription = subscription.find(
      (sub) => sub.name === 'Ingredient Inventory'
    );

    yield put(
      actModifier.setSubscriptionDetail({
        isActive: ingredientSubscription?.is_active,
        isSubscribing: ingredientSubscription?.is_subscribing,
      })
    );

    const outletIds = getSelectedOutletIdFromLocalStorage();
    clevertapLibraryModifier.libraryModifierStart({
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ currentOutlet: any; }' is not ... Remove this comment to see the full error message
      currentOutlet: outletIds[0],
    });

    yield call(fillSearchFormFromOutletIds, outletIds);

    const resModifier = yield call(apiModifier.getModifiers, { page: 1 });
    const modifiers = resModifier.data.results;
    yield put(actModifier.setModifiers(modifiers));
    yield put(actModifier.setTotalPage(resModifier.data.total_pages));
    yield put(actModifier.setCurrentPage(1));
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* onClickApplySetItem(action) {
  const {
    payload: { modifier },
  } = action;
  const params = {};
  try {
    yield put(act.showProgressBar());

    // set apply item data
    {
      const resItems = yield call(apiItems.getItems, params);
      const { data } = resItems;
      yield put(actModifier.applySetItemFillForm(modifier));
      yield put(actModifier.applySetItemSetCurrentPage(0));
      if (data.items) {
        yield put(actModifier.applySetItemSetTotalPage(data.total_pages));
        yield put(actModifier.applySetItemSetItems(data.items));
      } else {
        yield put(actModifier.applySetItemSetTotalPage(1));
        yield put(actModifier.applySetItemSetItems([]));
      }
      yield put(actModifier.showApplySetItem());
    }
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* applySetItemItemsRequest(request) {
  const {
    payload: { params, previousItems },
  } = request;
  try {
    yield put(act.showProgressBar());

    // set apply item data
    // if params contains keyword then we use other api
    // TODO the backend doesn't support pagination for search by keyword
    // so if we search with keyword, we cannot use pagination
    if (params && params.keyword) {
      // V1 API includes params with_batch to support pagination
      params.with_batch = true;
      const resItems = yield call(apiItems.getItemsV1, params);
      const { data } = resItems;
      yield put(actModifier.applySetItemSetCurrentPage(0));
      // set total page to 1 since V1 API doesn't support pagination
      yield put(actModifier.applySetItemSetTotalPage(1));
      const { items } = data;
      if (items) {
        yield put(actModifier.applySetItemSetItems(items));
      } else if (!data.total_count) {
        yield put(actModifier.applySetItemSetItems([]));
      }
    } else {
      const resItems = yield call(apiItems.getItems, params);
      const { data } = resItems;
      yield put(actModifier.applySetItemSetCurrentPage(params.page));
      if (data.items) {
        yield put(actModifier.applySetItemSetTotalPage(data.total_pages));
        const items = previousItems.concat(data.items);
        yield put(actModifier.applySetItemSetItems(items));
      }
    }
    yield put(actModifier.showApplySetItem());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* cancelApplySetItem() {
  yield put(actModifier.hideApplySetItemModal());
}

export function* confirmApplySetItem(request) {
  const {
    payload: { data, modifierId },
  } = request;
  try {
    yield put(act.showProgressBar());
    yield put(actModifier.hideApplySetItemModal());
    if (data.selectAllItems) {
      yield call(
        apiModifier.postApplyToItems,
        modifierId,
        {},
        { selectAllItems: data.selectAllItems }
      );
    } else {
      yield call(apiModifier.postApplyToItems, modifierId, data);
    }
    yield put(actModifier.setModifierDetail(null));
    yield call(closeRightColumn);
    const resModifier = yield call(apiModifier.getModifiers, { page: 1 });
    const modifiers = resModifier.data.results;
    yield put(actModifier.setModifiers(modifiers));
    yield put(actModifier.setTotalPage(resModifier.data.total_pages));
    yield put(actModifier.setCurrentPage(1));
    yield put(
      actNotif.success(
        'Successfully applied modifier',
        yield getCloneOutletNotification()
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(actModifier.closeSetAllItemConfirmation());
    yield put(act.hideProgressBar());
  }
}

export function* modifiersRequest(action) {
  const {
    payload: { params, previousModifiers },
  } = action;
  yield put(act.showProgressBar());

  try {
    const response = yield call(apiModifier.getModifiers, params);
    const modifiers = previousModifiers.concat(response.data.results);
    yield put(actModifier.setModifiers(modifiers));
    yield put(actModifier.setTotalPage(response.data.total_pages));
    yield put(actModifier.setCurrentPage(params.page));
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* onChangeOutletIds(action) {
  yield put(actModifier.setModifiers([]));
  yield put(showPageLoadingSpinner(true));
  const {
    payload: { outletIds },
  } = action;
  try {
    yield put(act.showProgressBar());
    yield call(closeRightColumn);
    setSelectedOutletIdToLocalStorage(outletIds);
    yield put(setSelectedOutletIds(outletIds));
    yield put(change('search', 'outletIds', outletIds));
    const res = yield call(apiModifier.getModifiers, { page: 1 });
    yield put(actModifier.emptyModifierForm());
    yield put(actModifier.setModifiers(res.data.results));
    yield put(actModifier.setTotalPage(res.data.total_pages));
    yield put(actModifier.setCurrentPage(1));
    yield put(actOutletList.checkCloningOutletOnProgress());
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
    yield put(showPageLoadingSpinner(false));
  }
}

export function* clickModifierRow(request) {
  const {
    payload: { modifier },
  } = request;
  try {
    yield put(act.showProgressBar());

    const res = yield call(apiModifier.getModifierById, modifier.id);

    yield call(setModifierForm, res.data);
    yield call(openRightColumn);
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

function* trackModifierStockSuccess(modifier) {
  const user = yield select(getUser);
  const { name: modifierName, track_stock: trackStock, options } = modifier;

  if (!trackStock) return;

  const ingredientOptions = options.filter(
    (option) => !!option.ingredient?.name
  );

  clevertapLibraryModifier.modifierStockSuccess(user.email);

  ingredientOptions.forEach((option) => {
    clevertapLibraryModifier.modifierStockCount(
      modifierName,
      option.ingredient.name
    );
  });
}

export function* submitFormCreate(request) {
  const {
    payload: { data, previousModifiers },
  } = request;

  if (data.modifier.options.length === 0) {
    yield put(
      actNotif.error(
        'Please save at least one option',
        yield getCloneOutletNotification()
      )
    );
  } else {
    const dataPayload = data;
    dataPayload.modifier.options = data.modifier.options.map((datum) => {
      if (datum.price) {
        return datum;
      }
      return { ...datum, price: null };
    });
    yield put(act.showProgressBar());
    yield put(actModifier.isSubmittingForm(true));
    try {
      const response = yield call(
        apiModifier.postModifier,
        dataPayload.modifier
      );
      // set to reducers
      const modifier = response.data;
      const modifiers = [modifier];
      previousModifiers.forEach((g) => modifiers.push(g));

      yield call(trackModifierStockSuccess, dataPayload.modifier);

      yield put(
        actNotif.success(
          'Modifier successfully created.',
          yield getCloneOutletNotification()
        )
      );
      yield put(actModifier.setModifiers(modifiers));
      yield call(setModifierForm, modifier);
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(
        actNotif.error(error.message, yield getCloneOutletNotification())
      );
    } finally {
      yield put(act.hideProgressBar());
      yield put(actModifier.isSubmittingForm(false));
    }
  }
}

export function* submitFormCreateAllOutlet(request) {
  const {
    payload: { data, previousModifiers },
  } = request;
  if (data.modifier.options.length === 0) {
    yield put(
      actNotif.error(
        'Please save at least one option',
        yield getCloneOutletNotification()
      )
    );
  } else {
    yield put(act.showProgressBar());
    yield put(actModifier.isSubmittingForm(true));
    try {
      const response = yield call(
        apiModifier.postCreateAllOutlet,
        data.modifier
      );
      // set to reducers
      const modifier = response.data;
      const modifiers = [modifier];
      previousModifiers.forEach((g) => modifiers.push(g));

      yield call(trackModifierStockSuccess, data.modifier);

      yield put(
        actNotif.success(
          'Modifiers successfully created.',
          yield getCloneOutletNotification()
        )
      );
      yield put(actModifier.setModifiers(modifiers));
      yield call(setModifierForm, modifier);
    } catch (error) {
      assertErrorHasMessage(error);
      if (error.message === ErrorCode.ALL_OUTLET_INGREDIENT_NOT_FOUND) {
        yield put(actModifier.showUnableToSaveAllOutletModal());
      } else {
        yield put(
          actNotif.error(error.message, yield getCloneOutletNotification())
        );
      }
    } finally {
      yield put(act.hideProgressBar());
      yield put(actModifier.isSubmittingForm(false));
    }
  }
}

export function* submitFormUpdate(request) {
  const {
    payload: { data, modifierId, previousModifiers },
  } = request;
  if (data.modifier.options.length === 0) {
    yield put(
      actNotif.error(
        'Please save at least one option',
        yield getCloneOutletNotification()
      )
    );
  } else {
    yield put(act.showProgressBar());
    yield put(actModifier.isSubmittingForm(true));
    try {
      const response = yield call(
        apiModifier.putModifier,
        modifierId,
        data.modifier
      );
      // set to reducers
      const modifier = response.data;
      const modifiers: any[] = [];
      previousModifiers.forEach((g) => {
        if (g.id === modifierId) {
          modifiers.push(modifier);
        } else {
          modifiers.push(g);
        }
      });

      yield call(trackModifierStockSuccess, data.modifier);

      yield put(
        actNotif.success(
          'Modifier successfully updated.',
          yield getCloneOutletNotification()
        )
      );
      yield put(actModifier.setModifiers(modifiers));
      yield call(setModifierForm, modifier);
    } catch (error) {
      assertErrorHasMessage(error);
      yield put(
        actNotif.error(error.message, yield getCloneOutletNotification())
      );
    } finally {
      yield put(act.hideProgressBar());
      yield put(actModifier.isSubmittingForm(false));
    }
  }
}

export function* deleteModifier(request) {
  const {
    payload: { modifierId, previousModifiers },
  } = request;
  yield put(act.showProgressBar());
  try {
    yield call(apiModifier.deleteModifier, modifierId);
    // set to reducers

    const modifiers: any[] = [];
    previousModifiers.forEach((g) => {
      if (g.id !== modifierId) {
        modifiers.push(g);
      }
    });
    yield put(actModifier.setModifiers(modifiers));
    yield put(actModifier.setModifierDetail(null));
    yield put(actModifier.setDeletedModifier(null));
    yield put(actModifier.hideDeleteModifierModal());
    yield call(closeRightColumn);
    yield put(
      actModifier.fillModifierForm({
        id: null,
        amount: null,
        quantity: null,
        active_options: [],
      })
    );

    yield put(
      actNotif.success(
        'Modifier successfully deleted.',
        yield getCloneOutletNotification()
      )
    );
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
  } finally {
    yield put(act.hideProgressBar());
  }
}

export function* clickCancel() {
  yield put(actModifier.setModifierDetail(null));
  yield put(actModifier.emptyModifierForm());
  yield call(closeRightColumn);
}

export function* clickCreateModifier() {
  yield call(setModifierForm, {
    modifier_options: [],
    active_options: [],
  });
  yield put(actModifier.emptyModifierForm());
  yield call(openRightColumn);
}

export function* askDeleteModifier(request) {
  const {
    payload: { deletedModifier },
  } = request;
  yield put(actModifier.setDeletedModifier(deletedModifier));
  yield put(actModifier.showDeleteModifierModal());
}

export function* cancelDeleteModifier() {
  yield put(actModifier.setDeletedModifier(null));
  yield put(actModifier.hideDeleteModifierModal());
}

export function* onClickManageModifierOptions(request) {
  const {
    payload: { previousModifierForm },
  } = request;
  yield put(actModifier.showModifierOptionsModal());
  yield put(actModifier.setPreviousModifierForm(previousModifierForm));
}

export function* onCancelManageModifierOptions(request) {
  const {
    payload: { previousModifierForm },
  } = request;
  yield put(arrayRemoveAll('modifier', 'options'));
  for (let idx = 0; idx < previousModifierForm.options.length; idx++) {
    yield put(
      arrayPush('modifier', 'options', previousModifierForm.options[idx])
    );
  }
  yield put(actModifier.hideModifierOptionsModal());
}

export function* onConfirmManageModifierOptions(request) {
  const { options } = request.payload;
  if (options.length === 0) {
    yield put(
      actNotif.error(
        'Please save at least one option.',
        yield getCloneOutletNotification()
      )
    );
  }
  yield put(actModifier.hideModifierOptionsModal());
}

export function* fetchIngredientsList({ payload }) {
  try {
    yield put(reset('ingredientsList'));

    // TODO:
    // Call the infinite scroll fetch handler
    yield call(ingredientsListInfiniteScroll, { ...payload, per_page: 100 });
  } catch (error) {
    assertErrorHasMessage(error);
    throw Error(error.message);
  }
}

// This is the saga worker when opening ingredients list popup
export function* onGetIngredientsList() {
  try {
    yield put(
      actModifier.setIngredientsList({
        results: [],
        total_pages: 0,
        total_count: 0,
        currentPage: 1,
        isLoading: true,
        isEmpty: false,
      })
    );
    yield call(fetchIngredientsList, {
      payload: {
        page: 1,
        previousItems: [],
        per_page: 100,
        keyword: '',
      },
    });
  } catch (error) {
    assertErrorHasMessage(error);
    yield put(
      actNotif.error(error.message, yield getCloneOutletNotification())
    );
    throw Error(error.message);
  }
}

export default function* saga() {
  yield takeLatest(LIBRARY_MODIFIER_PAGE_REQUEST, pageRequest);
  yield takeLatest(LIBRARY_MODIFIER_ON_CHANGE_OUTLET_IDS, onChangeOutletIds);
  yield takeLatest(LIBRARY_MODIFIER_MODIFIERS_REQUEST, modifiersRequest);
  yield takeLatest(LIBRARY_MODIFIER_ON_CLICK_CANCEL, clickCancel);
  yield takeLatest(LIBRARY_MODIFIER_ON_CLICK_ROW, clickModifierRow);
  yield takeLatest(LIBRARY_MODIFIER_SUBMIT_FORM_CREATE, submitFormCreate);
  yield takeLatest(
    LIBRARY_MODIFIER_ON_CLICK_CREATE_MODIFIER,
    clickCreateModifier
  );
  yield takeLatest(LIBRARY_MODIFIER_DELETE_MODIFIER, deleteModifier);
  yield takeLatest(LIBRARY_MODIFIER_ASK_DELETE_MODIFIER, askDeleteModifier);
  yield takeLatest(LIBRARY_MODIFIER_SUBMIT_FORM_UPDATE, submitFormUpdate);
  yield takeLatest(
    LIBRARY_MODIFIER_CANCEL_DELETE_MODIFIER,
    cancelDeleteModifier
  );
  yield takeLatest(
    LIBRARY_MODIFIER_SUBMIT_FORM_CREATE_ALL_OUTLET,
    submitFormCreateAllOutlet
  );
  yield takeLatest(
    LIBRARY_MODIFIER_ON_CLICK_APPLY_SET_ITEM,
    onClickApplySetItem
  );
  yield takeLatest(LIBRARY_MODIFIER_CANCEL_APPLY_SET_ITEM, cancelApplySetItem);
  yield takeLatest(
    LIBRARY_MODIFIER_CONFIRM_APPLY_SET_ITEM,
    confirmApplySetItem
  );
  yield takeLatest(
    LIBRARY_MODIFIER_APPLY_SET_ITEM_FILL_FORM,
    fillApplySetItemFormFlow
  );
  yield takeLatest(
    LIBRARY_MODIFIER_APPLY_SET_ITEM_ITEMS_REQUEST,
    applySetItemItemsRequest
  );
  yield takeLatest(LIBRARY_MODIFIER_FILL_MODIFIER_FORM, fillModifierFormFlow);
  yield takeLatest(LIBRARY_MODIFIER_EMPTY_MODIFIER_FORM, emptyModifierFormFlow);
  yield takeLatest(
    LIBRARY_MODIFIER_ON_CLICK_MANAGE_MODIFIER_OPTIONS,
    onClickManageModifierOptions
  );
  yield takeLatest(
    LIBRARY_MODIFIER_ON_CONFIRM_MANAGE_MODIFIER_OPTIONS,
    onConfirmManageModifierOptions
  );
  yield takeLatest(
    LIBRARY_MODIFIER_ON_CANCEL_MANAGE_MODIFIER_OPTIONS,
    onCancelManageModifierOptions
  );
  yield takeLatest(LIBRARY_MODIFIER_GET_INGREDIENTS_LIST, onGetIngredientsList);
  // @ts-expect-error
  yield takeLatest(
    LIBRARY_MODIFIER_FETCH_INGREDIENTS_LIST,
    fetchIngredientsList
  );
}
